import { UrgencyDetailFilters } from '../../services/UrgencySummaryController'

/**
 * Lo que viaja en `navigate(..., { state })` del resumen al detalle. Los filtros nunca van en la URL:
 * si se recarga sin `state`, el detalle vuelve al resumen.
 */
export interface UrgencyDetailRouteState {
  /** Lo que el backend aplica siempre: el período y, solo si el nombre no se conocía, la guardia o el financiador. */
  filters: UrgencyDetailFilters
  seed: UrgencyDetailSeed
}

/**
 * Los filtros de columna con los que arranca la tabla del detalle: lo que se abrió desde el resumen. Se ven y se quitan en la
 * propia tabla (null o vacío = sin ese filtro). Guardia, financiador y profesional filtran por el texto («contiene»).
 */
export interface UrgencyDetailSeed {
  guardia: string | null
  financierName: string | null
  evolutionProfessional: string | null
  /** Grupos de estado (ver `urgencyStateGroups`). */
  stateGroups: number[]
  priorityId: number | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || typeof value === 'number'
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

export function isUrgencyDetailRouteState(value: unknown): value is UrgencyDetailRouteState {
  if (!isRecord(value) || !isRecord(value.filters) || !isRecord(value.seed)) return false
  const { filters, seed } = value
  return typeof filters.from === 'string'
    && typeof filters.to === 'string'
    && isNullableNumber(filters.templateId)
    && isNullableNumber(filters.priorityId)
    && isNullableNumber(filters.financierId)
    && typeof filters.noFinancier === 'boolean'
    && typeof filters.noProfessional === 'boolean'
    && isNullableString(seed.guardia)
    && isNullableString(seed.financierName)
    && isNullableString(seed.evolutionProfessional)
    && Array.isArray(seed.stateGroups)
    && seed.stateGroups.every((id) => typeof id === 'number')
    && isNullableNumber(seed.priorityId)
}

/** Lo que una fila del resumen pide abrir en el detalle. Lo que no se informa sale de los filtros aplicados. */
export interface UrgencyDrill {
  /** Grupos de estado de la fila (ver `urgencyStateGroups`); sin informar, las llegadas (todos menos los duplicados). */
  stateGroups?: readonly number[]
  financierId?: number | null
  financierName?: string | null
  noFinancier?: boolean
  /** Fila de la tabla por profesional: sus atenciones (una atención cuenta una sola vez). */
  professionalName?: string | null
  noProfessional?: boolean
  /** Pisa la guardia aplicada (fila de la tabla por guardia). */
  templateId?: number | null
  templateName?: string | null
}
